<template>
  <header class="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-40 h-16 flex-shrink-0">
    <div class="h-full flex items-center justify-between px-4 sm:px-6 lg:px-8">
      <!-- Left: Company Info -->
      <div class="flex items-center gap-3">
        <h1 class="text-lg font-semibold text-gray-900 dark:text-white">{{ companyName }}</h1>
      </div>

      <!-- Right: Actions -->
      <div class="flex items-center gap-4 relative">
        <!-- Notifications -->
        <button
          ref="bellBtnRef"
          class="relative p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition"
          :title="bellTitle"
          @click="toggleNotifications"
        >
          <i class="pi pi-bell text-lg"></i>
          <span
            v-if="notificationCount > 0"
            class="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center ring-2 ring-white dark:ring-gray-800"
          >
            {{ notificationCount > 99 ? '99+' : notificationCount }}
          </span>
        </button>

        <!-- Notifications Panel -->
        <Transition name="slide">
          <div
            v-if="showNotifications"
            ref="panelRef"
            class="absolute top-12 right-0 w-96 max-w-[calc(100vw-2rem)] bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 max-h-[28rem] overflow-y-auto z-50"
          >
            <div class="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between gap-2 sticky top-0 bg-white dark:bg-gray-800 z-10">
              <div class="flex items-center gap-2 min-w-0">
                <h3 class="font-semibold text-gray-900 dark:text-white">{{ t('common.notifications') }}</h3>
                <span
                  v-if="notificationCount > 0"
                  class="text-[11px] font-bold px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300"
                >
                  {{ notificationCount }}
                </span>
              </div>
              <button
                v-if="messages.length > 0"
                class="text-xs text-primary-600 dark:text-primary-400 hover:underline"
                @click="markAllMessagesRead"
              >
                {{ t('messages.markAllRead') }}
              </button>
            </div>

            <div v-if="notificationsLoading && notificationCount === 0" class="p-6 flex justify-center">
              <i class="pi pi-spin pi-spinner text-primary-500 text-xl" />
            </div>

            <template v-else>
              <!-- Bildirimler (manuel / sistem) -->
              <div v-if="messages.length > 0" class="divide-y divide-gray-200 dark:divide-gray-700">
                <div class="px-4 pt-3 pb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                  {{ t('common.notifications') }}
                </div>
                <button
                  v-for="m in messages"
                  :key="m.id"
                  class="w-full text-left p-4 hover:bg-primary-50/60 dark:hover:bg-primary-900/15 transition cursor-pointer"
                  @click="openMessage(m)"
                >
                  <p class="text-sm font-semibold text-gray-900 dark:text-white">{{ m.title }}</p>
                  <p class="text-sm text-gray-600 dark:text-gray-400 mt-1 line-clamp-3">{{ m.message }}</p>
                  <p class="text-xs text-gray-500 dark:text-gray-500 mt-2">{{ formatRelativeTime(m.createdAt) }}</p>
                </button>
              </div>

              <!-- Bekleyen onaylar -->
              <div v-if="notifications.length > 0" class="divide-y divide-gray-200 dark:divide-gray-700">
                <div class="px-4 pt-3 pb-1 flex items-center justify-between text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                  <span>{{ t('messages.pendingApprovals') }}</span>
                  <button class="normal-case text-primary-600 dark:text-primary-400 hover:underline" @click="goToAllRequests">
                    {{ t('messages.seeAll') }}
                  </button>
                </div>
                <button
                  v-for="n in notifications"
                  :key="n.id"
                  class="w-full text-left p-4 hover:bg-primary-50/60 dark:hover:bg-primary-900/15 transition cursor-pointer"
                  @click="openNotification(n)"
                >
                  <div class="flex items-start justify-between gap-2">
                    <p class="text-sm font-semibold text-gray-900 dark:text-white">{{ n.title }}</p>
                    <span
                      class="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded shrink-0"
                      :class="n.typeBadgeClass"
                    >
                      {{ n.typeLabel }}
                    </span>
                  </div>
                  <p class="text-sm text-gray-600 dark:text-gray-400 mt-1 line-clamp-2">
                    {{ n.description }}
                  </p>
                  <p class="text-xs text-gray-500 dark:text-gray-500 mt-2">{{ formatRelativeTime(n.createdAt) }}</p>
                </button>
              </div>

              <div v-if="notificationCount === 0" class="p-6 text-center text-sm text-gray-500 dark:text-gray-400">
                <i class="pi pi-check-circle text-3xl text-emerald-500 mb-2 block" />
                {{ t('messages.noNewNotifications') }}
              </div>
            </template>
          </div>
        </Transition>

        <!-- Dil seçici (yalnızca çok dilli dağıtımda görünür) -->
        <LanguageSwitcher />

        <!-- Theme Toggle -->
        <button
          @click="toggleTheme"
          class="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition"
          :title="theme === 'light' ? t('common.darkTheme') : t('common.lightTheme')"
        >
          <i :class="theme === 'light' ? 'pi pi-moon' : 'pi pi-sun'" class="text-lg"></i>
        </button>

        <!-- User Menu -->
        <Menu :model="userMenuItems" popup>
          <template #start>
            <button class="flex items-center gap-2 p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition">
              <Avatar :label="userInitials" :style="{ backgroundColor: '#488BE6' }" shape="circle" size="small" />
              <span class="hidden sm:inline text-sm font-medium text-gray-900 dark:text-white">{{ userName }}</span>
              <i class="pi pi-chevron-down text-xs text-gray-600 dark:text-gray-400"></i>
            </button>
          </template>
        </Menu>
      </div>
    </div>
  </header>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import Menu from 'primevue/menu'
import Avatar from 'primevue/avatar'
import { useUIStore } from '@/stores/ui.store'
import { useAuthStore } from '@/stores/auth.store'
import { useAuth } from '@/composables/useAuth'
import { useFormatters } from '@/composables/useFormatters'
import { useSettingsStore } from '@/stores/settings.store'
import { requestsService } from '@/services/requests.service'
import { notificationsService, type AppNotification } from '@/services/notifications.service'
import LanguageSwitcher from '@/components/common/LanguageSwitcher.vue'
import { canUserDecideOnRequest } from '@/composables/useRequestWorkflow'
import { RequestType, UserRole } from '@/types/enums'
import type { Request as HrRequest } from '@/types/request.types'

interface PendingNotification {
  id: string
  title: string
  description: string
  createdAt: string
  routeTo: { path: string; query?: Record<string, string> }
  typeLabel: string
  typeBadgeClass: string
}

const { t } = useI18n()
const router = useRouter()
const uiStore = useUIStore()
const authStore = useAuthStore()
const settingsStore = useSettingsStore()
const { logout } = useAuth()
const { getInitials, formatRelativeTime } = useFormatters()

const showNotifications = ref(false)
const notifications = ref<PendingNotification[]>([])
const messages = ref<AppNotification[]>([])
const notificationsLoading = ref(false)
const bellBtnRef = ref<HTMLButtonElement | null>(null)
const panelRef = ref<HTMLDivElement | null>(null)
let pollTimer: ReturnType<typeof setInterval> | null = null

const theme = computed(() => uiStore.theme)
const userName = computed(() => authStore.user?.firstName || t('common.user'))
const userInitials = computed(() => {
  if (authStore.user) {
    return getInitials(authStore.user.firstName, authStore.user.lastName)
  }
  return 'U'
})

const companyName = computed(() => settingsStore.companySettings?.companyName || t('app.hrManagement'))
const notificationCount = computed(() => notifications.value.length + messages.value.length)
const bellTitle = computed(() =>
  notificationCount.value > 0
    ? `${notificationCount.value} bildirim`
    : 'Bildirim yok',
)

const NON_APPROVER_ROLES: UserRole[] = [UserRole.EMPLOYEE]

function canFetchPending(): boolean {
  const role = authStore.user?.role
  if (!role) return false
  return !NON_APPROVER_ROLES.includes(role)
}

function formatRequestTitle(req: HrRequest): {
  title: string
  description: string
  typeLabel: string
  typeBadgeClass: string
} {
  const fullName = `${req.employee?.firstName ?? ''} ${req.employee?.lastName ?? ''}`.trim() ||
    t('common.employee')
  let typeLabel = t('requestTypes.request')
  let typeBadgeClass = 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200'
  if (req.type === RequestType.ADVANCE) {
    typeLabel = t('requestTypes.advance')
    typeBadgeClass = 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
  } else if (req.type === RequestType.LEAVE_PAID) {
    typeLabel = t('requestTypes.leave')
    typeBadgeClass = 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300'
  } else if (req.type === RequestType.LEAVE_UNPAID) {
    typeLabel = t('requestTypes.unpaid')
    typeBadgeClass = 'bg-slate-100 text-slate-700 dark:bg-slate-700/40 dark:text-slate-300'
  } else if (req.type === RequestType.LEAVE_REPORT) {
    typeLabel = t('requestTypes.report')
    typeBadgeClass = 'bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-300'
  }

  let description: string
  if (req.type === RequestType.ADVANCE) {
    const amount = req.amount != null ? Number(req.amount).toLocaleString('tr-TR') + ' ₺' : ''
    description = `${fullName} avans talebi${amount ? ` · ${amount}` : ''}`
  } else {
    const start = req.startDate ? new Date(req.startDate).toLocaleDateString('tr-TR') : ''
    const end = req.endDate ? new Date(req.endDate).toLocaleDateString('tr-TR') : ''
    const period = start && end && start !== end ? `${start} – ${end}` : start
    description = `${fullName}${period ? ` · ${period}` : ''}`
  }

  return {
    title: `${typeLabel} onayı bekliyor`,
    description,
    typeLabel,
    typeBadgeClass,
  }
}

async function loadPendingApprovals() {
  if (!canFetchPending()) {
    notifications.value = []
    return
  }
  const user = authStore.user
  if (!user) return
  notificationsLoading.value = true
  try {
    // Arka plan bildirim yoklaması — global yükleme çubuğunu tetiklemesin
    const res = await requestsService.getPending({ skipLoading: true })
    const list = (res?.data || res || []) as HrRequest[]
    const filtered = list.filter((r) =>
      canUserDecideOnRequest(r, { id: user.id, role: user.role as UserRole }),
    )
    notifications.value = filtered.slice(0, 50).map((r) => {
      const { title, description, typeLabel, typeBadgeClass } = formatRequestTitle(r)
      return {
        id: r.id,
        title,
        description,
        createdAt: (r.createdAt as unknown as string) || new Date().toISOString(),
        routeTo: { path: '/dashboard/requests', query: { id: r.id } },
        typeLabel,
        typeBadgeClass,
      }
    })
  } catch {
    // sessizce geç: yetki yok veya kısa süreli ağ sorunu
  } finally {
    notificationsLoading.value = false
  }
}

async function loadMessages() {
  try {
    messages.value = await notificationsService.getUnread()
  } catch {
    // sessizce geç
  }
}

function refreshBell() {
  void loadPendingApprovals()
  void loadMessages()
}

async function openMessage(m: AppNotification) {
  try {
    await notificationsService.markRead(m.id)
  } catch {
    /* yine de listeden kaldır */
  }
  messages.value = messages.value.filter((x) => x.id !== m.id)
}

async function markAllMessagesRead() {
  const current = [...messages.value]
  messages.value = []
  await Promise.all(
    current.map((m) => notificationsService.markRead(m.id).catch(() => undefined)),
  )
}

function toggleNotifications() {
  showNotifications.value = !showNotifications.value
  if (showNotifications.value) {
    refreshBell()
  }
}

function openNotification(n: PendingNotification) {
  showNotifications.value = false
  router.push(n.routeTo).catch(() => undefined)
}

function goToAllRequests() {
  showNotifications.value = false
  router.push({ path: '/dashboard/requests' }).catch(() => undefined)
}

function onDocumentClick(e: MouseEvent) {
  if (!showNotifications.value) return
  const target = e.target as Node | null
  if (!target) return
  if (panelRef.value?.contains(target)) return
  if (bellBtnRef.value?.contains(target)) return
  showNotifications.value = false
}

onMounted(() => {
  refreshBell()
  pollTimer = setInterval(() => {
    refreshBell()
  }, 60_000)
  document.addEventListener('click', onDocumentClick)
})

onUnmounted(() => {
  if (pollTimer) clearInterval(pollTimer)
  document.removeEventListener('click', onDocumentClick)
})

const userMenuItems = computed(() => [
  {
    label: t('g.profilim'),
    icon: 'pi pi-user',
    command: () => {
      // Navigate to profile
    }
  },
  {
    label: t('common.settings'),
    icon: 'pi pi-cog',
    command: () => {
      // Navigate to user settings
    }
  },
  {
    label: t('common.changePassword'),
    icon: 'pi pi-lock',
    command: () => {
      // Show change password dialog
    }
  },
  { separator: true },
  {
    label: t('common.logoutShort'),
    icon: 'pi pi-sign-out',
    command: logout
  }
])

function toggleTheme() {
  uiStore.toggleTheme()
}
</script>

<style scoped>
:deep(.p-menu) {
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
}
</style>
